import React, { useEffect, useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  UserGroupIcon,
  FireIcon,
  TruckIcon,
  XMarkIcon,
  BanknotesIcon,
  CreditCardIcon,
  MapPinIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline'
import { supabase } from '../../config/supabase'
import UnifiedOrderService from '../../services/unifiedOrderService'

// Import status videos
import assignedVideo from '../../assets/Assigned.mp4'
import preparingVideo from '../../assets/preparing.mp4'
import preparedVideo from '../../assets/prepared.mp4'
import servedVideo from '../../assets/served.mp4'

const OrderTracking = ({ sessionId, orderId, isOpen, onClose }) => {
  const [orders, setOrders] = useState([])
  const [currentOrder, setCurrentOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [subscription, setSubscription] = useState(null)
  const [isOrderItemsExpanded, setIsOrderItemsExpanded] = useState(false)
  
  // Use ref to track current order selection to avoid closure issues in polling
  const currentOrderRef = useRef(null)

  // Load customer orders when component opens
  useEffect(() => {
    let pollingInterval = null

    if (isOpen) {
      if (orderId) {
        // Load specific order by ID
        loadOrderById(orderId)
        setupOrderSubscription(orderId)
        
        // Set up polling as fallback (every 5 seconds)
        // This ensures updates even if real-time subscription fails
        pollingInterval = setInterval(() => {
          console.log('🔄 Polling for order updates...')
          loadOrderById(orderId, true) // Skip loading state to prevent blinking
        }, 5000)
      } else if (sessionId) {
        // Load all orders for session
        loadCustomerOrders()
        setupRealtimeSubscription()
        
        // Set up polling for session orders
        pollingInterval = setInterval(() => {
          console.log('🔄 Polling for session orders...')
          loadCustomerOrders(true) // Skip loading state to prevent blinking
        }, 5000)
      }
    }

    return () => {
      if (subscription) {
        console.log('🧹 Cleaning up subscription...')
        subscription.unsubscribe()
      }
      if (pollingInterval) {
        console.log('🧹 Cleaning up polling interval...')
        clearInterval(pollingInterval)
      }
    }
  }, [isOpen, sessionId, orderId])

  const loadOrderById = async (id, skipLoading = false) => {
    try {
      // Only show loading on initial load, not on updates
      if (!skipLoading) {
        setLoading(true)
      }
      console.log('📋 Loading order by ID:', id)
      
      // Fetch specific order from database
      const { data: order, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            *,
            menu_items (name, image_url, price)
          )
        `)
        .eq('id', id)
        .single()

      if (error) throw error

      console.log('✅ Order loaded:', order)
      setCurrentOrder(order)
      setOrders([order])
    } catch (error) {
      console.error('❌ Error loading order:', error)
    } finally {
      if (!skipLoading) {
        setLoading(false)
      }
    }
  }

  const setupOrderSubscription = (id) => {
    try {
      console.log('🔔 Setting up realtime subscription for order:', id)
      
      // Subscribe to order updates
      const channel = supabase
        .channel(`order-tracking-${id}`, {
          config: {
            broadcast: { self: true }
          }
        })
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'orders',
            filter: `id=eq.${id}`
          },
          (payload) => {
            console.log('📨 Order update received:', payload)
            console.log('📨 New status:', payload.new?.status)
            console.log('📨 Old status:', payload.old?.status)
            
            // Update the current order with new data
            if (payload.new) {
              setCurrentOrder(prev => ({
                ...prev,
                ...payload.new
              }))
            }
            
            // Also reload full order data to get all related info (skip loading state)
            loadOrderById(id, true)
          }
        )
        .subscribe((status, err) => {
          if (status === 'SUBSCRIBED') {
            console.log('✅ Successfully subscribed to order updates')
          } else if (status === 'CHANNEL_ERROR') {
            console.error('❌ Channel error:', err)
          } else if (status === 'TIMED_OUT') {
            console.error('⏰ Subscription timed out')
          } else {
            console.log('📡 Subscription status:', status)
          }
        })

      setSubscription(channel)
    } catch (error) {
      console.error('❌ Error setting up subscription:', error)
    }
  }

  const loadCustomerOrders = async (skipLoading = false) => {
    try {
      // Only show loading on initial load, not on updates
      if (!skipLoading) {
        setLoading(true)
      }
      
      console.log('🔍 Loading all customer orders...')
      
      // Try to get customer info from localStorage
      const guestSession = localStorage.getItem('ordyrr_guest')
      const authSession = localStorage.getItem('ordyrr_customer')
      
      // Also check Supabase auth session for Google Sign-In users
      const { data: { session: supabaseSession } } = await supabase.auth.getSession()
      
      let customerOrders = []
      let customerId = null
      let customerPhone = null
      let isAuthenticated = false
      
      // Determine if user is authenticated and get their info
      if (supabaseSession?.user) {
        // Google Sign-In or authenticated user
        customerId = supabaseSession.user.id
        isAuthenticated = true
        console.log('✅ Authenticated user detected (Supabase):', customerId)
      } else if (authSession) {
        // Email/password authenticated user
        const authData = JSON.parse(authSession)
        customerId = authData.id
        isAuthenticated = true
        console.log('✅ Authenticated user detected (localStorage):', customerId)
      } else if (guestSession) {
        // Guest user
        const guestData = JSON.parse(guestSession)
        customerId = guestData.id
        customerPhone = guestData.phone
        isAuthenticated = false
        console.log('👤 Guest user detected:', { customerId, customerPhone })
      }
      
      // PRIORITY 1: For authenticated users, query by customer_id FIRST
      // PRIORITY 2: For guest users, use sessionId or customer_phone
      if (isAuthenticated && customerId) {
        console.log('📋 Fetching orders by customer_id (authenticated user):', customerId)
        
        // Fetch all active orders for this authenticated customer
        let query = supabase
          .from('orders')
          .select(`
            *,
            order_items (
              *,
              menu_items (name, image_url, price)
            )
          `)
          .eq('customer_id', customerId)
          .not('status', 'in', '(completed,cancelled,served)')
          .order('created_at', { ascending: false })
        
        const { data, error } = await query
        
        if (error) {
          console.error('❌ Error fetching customer orders:', error)
        } else {
          customerOrders = data || []
          console.log('✅ Found orders by customer_id:', customerOrders.length)
        }
      } else if (sessionId) {
        // Guest user: Use sessionId
        console.log('📋 Fetching orders by sessionId (guest user):', sessionId)
        customerOrders = await UnifiedOrderService.getCustomerOrdersBySession(sessionId)
      } else if (customerPhone) {
        // Guest user: Fallback to customer_phone
        console.log('📋 Fetching orders by customer_phone (guest user):', customerPhone)
        
        let query = supabase
          .from('orders')
          .select(`
            *,
            order_items (
              *,
              menu_items (name, image_url, price)
            )
          `)
          .eq('customer_phone', customerPhone)
          .not('status', 'in', '(completed,cancelled,served)')
          .order('created_at', { ascending: false })
        
        const { data, error } = await query
        
        if (error) {
          console.error('❌ Error fetching customer orders:', error)
        } else {
          customerOrders = data || []
          console.log('✅ Found orders by customer_phone:', customerOrders.length)
        }
      }

      // Filter out delivered/served/completed/cancelled orders
      const activeOrders = (customerOrders || []).filter(
        order => !['delivered', 'served', 'completed', 'cancelled'].includes(order.status)
      )
      
      console.log('📋 Total customer orders loaded:', customerOrders?.length || 0)
      console.log('📋 Active orders (filtered):', activeOrders.length)
      setOrders(activeOrders)
      
      // Set the most recent order as current ONLY if currentOrder is not set or is not in the active orders
      if (activeOrders && activeOrders.length > 0) {
        // Use ref to get the latest selected order ID (avoids closure issues)
        const currentOrderId = currentOrderRef.current?.id
        const currentOrderStillActive = currentOrderId && activeOrders.find(o => o.id === currentOrderId)
        
        console.log('🔍 Current order ID (from ref):', currentOrderId)
        console.log('🔍 Current order still active:', !!currentOrderStillActive)
        
        if (!currentOrderStillActive) {
          // Only update currentOrder if the current one is no longer active
          console.log('⚠️ Current order not found in active orders, switching to first order')
          setCurrentOrder(activeOrders[0])
          currentOrderRef.current = activeOrders[0]
        } else {
          // Update the current order with fresh data but keep it selected
          console.log('✅ Keeping current order selection, updating with fresh data')
          const updatedCurrentOrder = activeOrders.find(o => o.id === currentOrderId)
          setCurrentOrder(updatedCurrentOrder)
          currentOrderRef.current = updatedCurrentOrder
        }
      }
    } catch (error) {
      console.error('❌ Error loading customer orders:', error)
    } finally {
      if (!skipLoading) {
        setLoading(false)
      }
    }
  }

  const setupRealtimeSubscription = async () => {
    try {
      // Subscribe to customer order updates using UnifiedOrderService
      const subscription = UnifiedOrderService.subscribeToCustomerOrders(sessionId, (payload) => {
        console.log('📨 Customer order update received:', payload)
        loadCustomerOrders() // Refresh orders
      })
      
      setSubscription(subscription)
    } catch (error) {
      console.error('❌ Error setting up realtime subscription:', error)
    }
  }

  const orderStatuses = [
    { 
      id: 'pending', 
      label: 'Order Pending', 
      icon: ClockIcon,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
      description: 'Your order is being processed'
    },
    { 
      id: 'confirmed', 
      label: 'Order Confirmed', 
      icon: CheckCircleIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      description: 'Your order has been confirmed'
    },
    { 
      id: 'assigning', 
      label: 'Assigning Staff', 
      icon: ClockIcon,
      color: 'text-blue-500',
      bgColor: 'bg-blue-100',
      description: 'Finding the best staff member'
    },
    { 
      id: 'assigned', 
      label: 'Assigned to Staff', 
      icon: UserGroupIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      description: 'Staff member assigned to your order'
    },
    { 
      id: 'preparing', 
      label: 'Preparing', 
      icon: FireIcon,
      color: 'text-orange-500',
      bgColor: 'bg-orange-100',
      description: 'Your food is being prepared'
    },
    { 
      id: 'ready', 
      label: 'Ready', 
      icon: CheckCircleIcon,
      color: 'text-green-500',
      bgColor: 'bg-green-100',
      description: 'Your order is ready'
    },
    { 
      id: 'served', 
      label: 'Served', 
      icon: TruckIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      description: 'Enjoy your meal!'
    }
  ]

  const getStatusIndex = (status) => {
    return orderStatuses.findIndex(s => s.id === status)
  }

  const isStatusCompleted = (statusId) => {
    const currentIndex = getStatusIndex(currentOrder?.status || 'pending')
    const statusIndex = getStatusIndex(statusId)
    return statusIndex <= currentIndex
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
            <h3 className="text-lg font-bold text-gray-900">Order Tracking</h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your orders...</p>
            </div>
          ) : !currentOrder || orders.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="w-12 h-12 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">All Orders Served!</h3>
              <p className="text-sm text-gray-600">
                All your orders have been completed and served. Enjoy your meal! 🍽️
              </p>
            </div>
          ) : (
            <div className="px-4 pb-4">
              {/* Multiple Orders Switcher - Minimal Design - Only show active orders */}
              {orders.filter(o => !['delivered', 'served', 'completed', 'cancelled'].includes(o.status)).length > 1 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
                    {orders
                      .filter(o => !['delivered', 'served', 'completed', 'cancelled'].includes(o.status))
                      .map((order, index) => (
                        <button
                          key={order.id}
                          onClick={() => {
                            setCurrentOrder(order)
                            currentOrderRef.current = order
                          }}
                          className={`flex-shrink-0 px-3 py-2 rounded-lg border transition-all ${
                            currentOrder.id === order.id
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <div className="text-left">
                            <p className={`text-xs font-semibold ${
                              currentOrder.id === order.id ? 'text-green-600' : 'text-gray-700'
                            }`}>
                              Order {index + 1}
                            </p>
                            <p className="text-xs text-gray-500">
                              ₹{(order.total_amount || order.subtotal)?.toFixed(0)}
                            </p>
                          </div>
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* Order Info - Compact */}
              <div className="bg-gray-50 rounded-xl p-3 mb-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Order #{currentOrder.order_number}</p>
                    <p className="text-xs text-gray-500">
                      Placed at {new Date(currentOrder.created_at).toLocaleTimeString('en-US', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">₹{(currentOrder.total_amount || currentOrder.subtotal)?.toFixed(2)}</p>
                    {currentOrder.discount_amount > 0 && (
                      <p className="text-xs text-gray-500 line-through">₹{currentOrder.subtotal?.toFixed(2)}</p>
                    )}
                    <div className="flex items-center justify-end gap-1 text-xs text-gray-500 mt-0.5">
                      {currentOrder.payment_method === 'cash' ? (
                        <>
                          <BanknotesIcon className="h-3 w-3" />
                          <span>Pay at table</span>
                        </>
                      ) : (
                        <>
                          <CreditCardIcon className="h-3 w-3" />
                          <span>Online</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Tracking Timeline */}
              <div>
                {/* Estimated Time */}
                <div className="text-center mb-4">
                  <p className="text-xs text-gray-500 mb-1">Estimated serving time</p>
                  <p className="text-xl font-bold text-gray-900">
                    {currentOrder.estimated_time || '15-20'} min
                  </p>
                </div>

                {/* Progress Line with Stations - Minimal Design */}
                <div className="mb-6">
                  {/* Progress Bar Container */}
                  <div className="relative px-2">
                    {/* Background Line */}
                    <div className="absolute top-2 left-2 right-2 h-0.5 bg-gray-200 rounded-full" />
                    
                    {/* Active Progress Line */}
                    <motion.div
                      className="absolute top-2 left-2 h-0.5 bg-gradient-to-r from-green-500 to-green-400 rounded-full shadow-sm"
                      initial={false}
                      animate={{ 
                        width: `calc(${(getStatusIndex(currentOrder.status) / (orderStatuses.length - 1)) * 100}% - 8px)` 
                      }}
                      transition={{ 
                        duration: 1.2, 
                        ease: [0.4, 0, 0.2, 1],
                        type: "spring",
                        stiffness: 50,
                        damping: 20
                      }}
                    />
                    
                    {/* Status Stations */}
                    <div className="relative flex justify-between">
                      {orderStatuses.map((status, index) => {
                        const isCompleted = isStatusCompleted(status.id)
                        const isCurrent = currentOrder.status === status.id
                        
                        return (
                          <div key={status.id} className="flex flex-col items-center">
                            {/* Station Dot - Smaller */}
                            <motion.div
                              className={`w-4 h-4 rounded-full border flex items-center justify-center z-10 ${
                                isCurrent
                                  ? 'bg-green-500 border-green-500 shadow-md shadow-green-200'
                                  : isCompleted
                                  ? 'bg-green-500 border-green-500'
                                  : 'bg-white border-gray-300'
                              }`}
                              initial={false}
                              animate={
                                isCurrent 
                                  ? { scale: [1, 1.15, 1] } 
                                  : isCompleted 
                                  ? { scale: 1 }
                                  : { scale: 1 }
                              }
                              transition={
                                isCurrent 
                                  ? { repeat: Infinity, duration: 2 }
                                  : { duration: 0.5, ease: "easeOut" }
                              }
                            >
                              {isCompleted && !isCurrent && (
                                <div className="w-1.5 h-1.5 bg-white rounded-full" />
                              )}
                              {isCurrent && (
                                <motion.div
                                  className="w-1.5 h-1.5 bg-white rounded-full"
                                  animate={{ scale: [1, 1.3, 1] }}
                                  transition={{ repeat: Infinity, duration: 1 }}
                                />
                              )}
                            </motion.div>
                            
                            {/* Station Label - Only show for current */}
                            {isCurrent && (
                              <p className="text-[9px] mt-1.5 text-center max-w-[50px] font-medium text-green-600">
                                {status.label}
                              </p>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>

                {/* Current Status Animation Display */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-100">
                  {/* Pending Status */}
                  {currentOrder.status === 'pending' && (
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="text-center"
                    >
                      {/* Clock Animation with Pulse */}
                      <motion.div
                        className="relative w-16 h-16 mx-auto mb-3"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", duration: 0.5 }}
                      >
                        <motion.div
                          className="absolute inset-0 bg-yellow-100 rounded-full"
                          animate={{ scale: [1, 1.1, 1], opacity: [0.7, 1, 0.7] }}
                          transition={{ repeat: Infinity, duration: 2 }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <motion.div
                            animate={{ rotate: [0, 360] }}
                            transition={{ repeat: Infinity, duration: 3, ease: "linear" }}
                          >
                            <ClockIcon className="w-10 h-10 text-yellow-600" strokeWidth={2} />
                          </motion.div>
                        </div>
                      </motion.div>
                      <h3 className="text-base font-bold text-yellow-600 mb-0.5">Order Pending</h3>
                      <p className="text-xs text-gray-600 mb-2">Processing your order...</p>
                      <div className="bg-yellow-50 rounded-lg p-2.5 mt-3">
                        <p className="text-xs text-gray-700 leading-relaxed">
                          ⏳ Your order is being processed. Please wait while we confirm your order details.
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* Confirmed Status - Searching for Waiter */}
                  {currentOrder.status === 'confirmed' && (
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="text-center"
                    >
                      {/* Searching Animation with Multiple Dots */}
                      <motion.div
                        className="relative w-20 h-20 mx-auto mb-3"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", duration: 0.5 }}
                      >
                        {/* Outer Pulse Ring */}
                        <motion.div
                          className="absolute inset-0 bg-green-100 rounded-full"
                          animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                          transition={{ repeat: Infinity, duration: 2 }}
                        />
                        {/* Middle Ring */}
                        <motion.div
                          className="absolute inset-2 bg-green-200 rounded-full"
                          animate={{ scale: [1, 1.2, 1], opacity: [0.6, 0.2, 0.6] }}
                          transition={{ repeat: Infinity, duration: 2, delay: 0.3 }}
                        />
                        {/* Center Icon */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <motion.div
                            animate={{ rotate: [0, 360] }}
                            transition={{ repeat: Infinity, duration: 3, ease: "linear" }}
                          >
                            <UserGroupIcon className="w-10 h-10 text-green-600" strokeWidth={2} />
                          </motion.div>
                        </div>
                        {/* Orbiting Dots */}
                        {[0, 120, 240].map((angle, i) => (
                          <motion.div
                            key={i}
                            className="absolute w-2 h-2 bg-green-500 rounded-full"
                            style={{
                              top: '50%',
                              left: '50%',
                              marginTop: '-4px',
                              marginLeft: '-4px'
                            }}
                            animate={{
                              x: [0, Math.cos((angle * Math.PI) / 180) * 35],
                              y: [0, Math.sin((angle * Math.PI) / 180) * 35],
                              scale: [1, 0.5, 1],
                              opacity: [1, 0.3, 1]
                            }}
                            transition={{
                              repeat: Infinity,
                              duration: 2,
                              delay: i * 0.2,
                              ease: "easeInOut"
                            }}
                          />
                        ))}
                      </motion.div>
                      <h3 className="text-base font-bold text-green-600 mb-0.5">Searching for Waiter...</h3>
                      <p className="text-xs text-gray-600 mb-2">Finding available staff member</p>
                      <div className="bg-green-50 rounded-lg p-2.5 mt-3">
                        <p className="text-xs text-gray-700 leading-relaxed">
                          🔍 We're searching for a free waiter to handle your order. This usually takes just a moment!
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* Assigning Status */}
                  {currentOrder.status === 'assigning' && (
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="text-center"
                    >
                      {/* Clock/Search Animation */}
                      <div className="relative w-16 h-16 mx-auto mb-3">
                        <motion.div
                          className="absolute inset-0 bg-blue-100 rounded-full"
                          animate={{ rotate: 360 }}
                          transition={{ repeat: Infinity, duration: 3, ease: "linear" }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <motion.div
                            animate={{ rotate: [0, 360] }}
                            transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                          >
                            <ClockIcon className="w-10 h-10 text-blue-600" strokeWidth={2} />
                          </motion.div>
                        </div>
                      </div>
                      <h3 className="text-base font-bold text-blue-600 mb-0.5">Finding Staff...</h3>
                      <p className="text-xs text-gray-600 mb-2">Assigning staff member</p>
                      <div className="bg-blue-50 rounded-lg p-2.5 mt-3">
                        <p className="text-xs text-gray-700 leading-relaxed">
                          ⏳ We're matching your order with an available staff member who will prepare your food with care. This usually takes just a moment.
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* Assigned Status - Video Animation */}
                  {currentOrder.status === 'assigned' && (
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="text-center"
                    >
                      {/* Assigned Video */}
                      <div className="relative w-32 h-32 mx-auto mb-3 flex items-center justify-center rounded-full overflow-hidden bg-gray-100">
                        <video
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="w-full h-full object-cover"
                        >
                          <source src={assignedVideo} type="video/mp4" />
                        </video>
                      </div>
                      <h3 className="text-base font-bold text-blue-600 mb-0.5">Staff Assigned!</h3>
                      <p className="text-xs text-gray-600 mb-2">
                        {currentOrder.assigned_staff_name 
                          ? `${currentOrder.assigned_staff_name} will prepare your order`
                          : 'Staff member assigned'}
                      </p>
                      <div className="bg-blue-50 rounded-lg p-2.5 mt-3">
                        <p className="text-xs text-gray-700 leading-relaxed">
                          👨‍🍳 A staff member has been auto-assigned to your order. They will start preparing your food shortly. You can sit back and relax!
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* Preparing Status - Video Animation */}
                  {currentOrder.status === 'preparing' && (
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="text-center"
                    >
                      {/* Preparing Video */}
                      <div className="relative w-32 h-32 mx-auto mb-3 flex items-center justify-center rounded-full overflow-hidden bg-gray-100">
                        <video
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="w-full h-full object-cover scale-125"
                        >
                          <source src={preparingVideo} type="video/mp4" />
                        </video>
                      </div>
                      <h3 className="text-base font-bold text-orange-600 mb-0.5">Preparing Your Order</h3>
                      <p className="text-xs text-gray-600 mb-2">Your food is being cooked</p>
                      <div className="bg-orange-50 rounded-lg p-2.5 mt-3">
                        <p className="text-xs text-gray-700 leading-relaxed">
                          🍳 Your delicious meal is now being prepared in our kitchen! Our chef is cooking your order with fresh ingredients and lots of care. Won't be long now!
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* Prepared/Ready Status - Video Animation */}
                  {(currentOrder.status === 'prepared' || currentOrder.status === 'ready') && (
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="text-center"
                    >
                      {/* Prepared Video */}
                      <div className="relative w-32 h-32 mx-auto mb-3 flex items-center justify-center rounded-full overflow-hidden bg-gray-100">
                        <video
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="w-full h-full object-cover"
                        >
                          <source src={preparedVideo} type="video/mp4" />
                        </video>
                      </div>
                      <h3 className="text-base font-bold text-green-600 mb-0.5">Order Ready!</h3>
                      <p className="text-xs text-gray-600 mb-2">Ready to be served</p>
                      <div className="bg-green-50 rounded-lg p-2.5 mt-3">
                        <p className="text-xs text-gray-700 leading-relaxed">
                          ✅ Awesome! Your order is ready and looking delicious. Our staff will bring it to your table shortly. Get ready to enjoy your meal!
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* Served Status - Video Animation */}
                  {currentOrder.status === 'served' && (
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="text-center"
                    >
                      {/* Served Video */}
                      <div className="relative w-32 h-32 mx-auto mb-3 flex items-center justify-center rounded-full overflow-hidden bg-gray-100">
                        <video
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="w-full h-full object-cover"
                        >
                          <source src={servedVideo} type="video/mp4" />
                        </video>
                      </div>
                      <h3 className="text-base font-bold text-purple-600 mb-0.5">Bon Appétit! 🎉</h3>
                      <p className="text-xs text-gray-600 mb-2">Enjoy your meal!</p>
                      <div className="bg-purple-50 rounded-lg p-2.5 mt-3">
                        <p className="text-xs text-gray-700 leading-relaxed">
                          🍽️ Your order has been served! We hope you enjoy every bite. Don't forget to leave us a review and let us know how we did!
                        </p>
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Progress Indicator */}
                <div className="flex items-center justify-center gap-2 mt-6">
                  {orderStatuses.map((status, index) => {
                    const isCompleted = isStatusCompleted(status.id)
                    const isCurrent = currentOrder.status === status.id

                    return (
                      <motion.div
                        key={status.id}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        className={`w-3 h-3 rounded-full ${
                          isCurrent 
                            ? 'bg-blue-500 w-8' 
                            : isCompleted 
                              ? 'bg-green-500' 
                              : 'bg-gray-300'
                        }`}
                      />
                    )
                  })}
                </div>
              </div>

              {/* Order Items - Expandable */}
              {currentOrder.order_items && currentOrder.order_items.length > 0 && (
                <div className="space-y-3 mt-8">
                  {/* Toggle Text */}
                  <button
                    onClick={() => setIsOrderItemsExpanded(!isOrderItemsExpanded)}
                    className="w-full flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-green-600">View Ordered Items</span>
                      <span className="text-xs text-gray-500">({currentOrder.order_items.length} items)</span>
                    </div>
                    {isOrderItemsExpanded ? (
                      <ChevronUpIcon className="w-4 h-4 text-green-600" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 text-green-600" />
                    )}
                  </button>

                  {/* Expandable Items List */}
                  <AnimatePresence>
                    {isOrderItemsExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-3 pt-2">
                          {currentOrder.order_items.map((item, index) => (
                            <motion.div
                              key={index}
                              initial={{ x: -20, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              transition={{ delay: index * 0.1 }}
                              className="flex gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100"
                            >
                              {/* Item Image */}
                              {item.menu_items?.image_url && (
                                <div className="flex-shrink-0">
                                  <img
                                    src={item.menu_items.image_url}
                                    alt={item.item_name}
                                    className="w-20 h-20 object-cover rounded-lg"
                                    onError={(e) => {
                                      e.target.onerror = null
                                      e.target.src = 'https://via.placeholder.com/80x80?text=No+Image'
                                    }}
                                  />
                                </div>
                              )}
                              
                              {/* Item Details */}
                              <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-start gap-2">
                                  <div className="flex-1">
                                    <p className="font-semibold text-black text-sm leading-tight">
                                      {item.item_name}
                                    </p>
                                    <p className="text-sm text-gray-600 mt-1">
                                      Qty: {item.quantity}
                                    </p>
                                    {item.special_instructions && (
                                      <div className="mt-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                                        <p className="text-xs text-gray-700">
                                          <span className="font-medium">Note:</span> {item.special_instructions}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                  <p className="font-bold text-black text-sm whitespace-nowrap">
                                    ₹{item.total_price}
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                          
                          {/* Order Summary in Expanded View */}
                          <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                            <div className="flex justify-between items-center">
                              <span className="font-semibold text-gray-700">Total Amount</span>
                              <span className="font-bold text-green-600 text-lg">
                                ₹{currentOrder.total_amount}
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* All Orders History - Only show active orders (exclude delivered/served/completed/cancelled) */}
              {orders.filter(o => !['delivered', 'served', 'completed', 'cancelled'].includes(o.status)).length > 1 && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-black">Other Active Orders</h4>
                  <div className="space-y-2">
                    {orders
                      .filter(o => !['delivered', 'served', 'completed', 'cancelled'].includes(o.status))
                      .map((order, index) => (
                        <button
                          key={order.id}
                          onClick={() => {
                            setCurrentOrder(order)
                            currentOrderRef.current = order
                          }}
                          className={`w-full text-left p-3 rounded-lg border transition-all ${
                            currentOrder.id === order.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-medium text-black">Order #{order.order_number}</p>
                              <p className="text-sm text-gray-600">
                                {new Date(order.created_at).toLocaleString()}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-black">₹{order.total_amount}</p>
                              <p className={`text-sm capitalize ${
                                order.status === 'completed' ? 'text-green-600' :
                                order.status === 'cancelled' ? 'text-red-600' :
                                'text-blue-600'
                              }`}>
                                {order.status}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default OrderTracking
